/*
 * Tenants — expanded profile page (Feb-17).
 *
 * Captures full lease + contact + KYC (ID document) records for each
 * tenant so the operator has one screen with everything needed to
 * chase rent, send lease-renewal reminders, and reach next-of-kin.
 */
import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { useT } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, UserCheck, Phone, Mail, MessageCircle, Home, FileText, IdCard, Upload, X, Eye, Calendar, DollarSign, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import RowActions from '../components/RowActions';

const MANAGE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'CS_SUPERVISOR'];
const ID_TYPES = [
  { value: 'passport', label: 'Passport' },
  { value: 'emirates_id', label: 'Emirates ID' },
  { value: 'national_id', label: 'National ID' },
  { value: 'driving_license', label: 'Driving License' },
];
const REL_OPTIONS = ['Spouse', 'Parent', 'Child', 'Sibling', 'Employer', 'Friend', 'Relative', 'Other'];
const RENT_FREQS = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];
const UNIT_TYPES = ['studio', '1BR', '2BR', '3BR', '4BR', 'villa', 'office', 'retail'];

const EMPTY = {
  tenant_name: '', unit_number: '', building_id: '',
  contact_email: '', contact_phone: '', whatsapp_number: '',
  lease_start: '', lease_end: '', rent_amount: '', rent_currency: 'AED', rent_frequency: 'monthly',
  deposit_amount: '', deposit_paid: false, unit_type: '', unit_area_sqft: '',
  alternate_contact_name: '', alternate_contact_phone: '', alternate_contact_email: '', alternate_contact_relation: '',
  home_country: '', home_country_address: '', home_country_phone: '',
  id_document_type: 'passport', id_document_number: '', id_document_expiry: '',
  emirates_id: '', notes: '', status: 'active',
  id_document_urls: [],
};

const toISO = (d) => (d ? new Date(d).toISOString() : null);
const fromISO = (v) => (v ? String(v).slice(0, 10) : '');

export default function Tenants() {
  const t = useT();
  const { user } = useContext(AuthContext);
  const [items, setItems] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploadingIdx, setUploadingIdx] = useState(false);

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API}/tenants`, { withCredentials: true }),
        axios.get(`${API}/buildings`, { withCredentials: true })
      ]);
      setItems(r1.data); setBuildings(r2.data);
    } catch { toast.error('Failed to load tenants'); }
    setLoading(false);
  };

  const openNew = () => { setEditingId(null); setForm(EMPTY); setShowDialog(true); };
  const openEdit = (tn) => {
    setEditingId(tn.id);
    setForm({
      ...EMPTY, ...tn,
      lease_start: fromISO(tn.lease_start),
      lease_end: fromISO(tn.lease_end),
      id_document_expiry: fromISO(tn.id_document_expiry),
      rent_amount: tn.rent_amount ?? '',
      deposit_amount: tn.deposit_amount ?? '',
      unit_area_sqft: tn.unit_area_sqft ?? '',
      id_document_urls: tn.id_document_urls || [],
    });
    setShowDialog(true);
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });

  const uploadIdDoc = async (file) => {
    if (!file) return;
    setUploadingIdx(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await axios.post(`${API}/upload`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(prev => ({ ...prev, id_document_urls: [...(prev.id_document_urls || []), r.data.url] }));
      toast.success('Document uploaded');
    } catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); }
    setUploadingIdx(false);
  };

  const removeIdDoc = (idx) => setForm(prev => ({ ...prev, id_document_urls: prev.id_document_urls.filter((_, i) => i !== idx) }));

  const handleSave = async () => {
    const payload = {
      ...form,
      lease_start: toISO(form.lease_start),
      lease_end: toISO(form.lease_end),
      id_document_expiry: toISO(form.id_document_expiry),
      rent_amount: form.rent_amount !== '' ? parseFloat(form.rent_amount) : null,
      deposit_amount: form.deposit_amount !== '' ? parseFloat(form.deposit_amount) : null,
      unit_area_sqft: form.unit_area_sqft !== '' ? parseFloat(form.unit_area_sqft) : null,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/tenants/${editingId}`, payload, { withCredentials: true });
        toast.success('Tenant updated');
      } else {
        await axios.post(`${API}/tenants`, payload, { withCredentials: true });
        toast.success('Tenant added');
      }
      setShowDialog(false);
      setEditingId(null);
      setForm(EMPTY);
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this tenant record?')) return;
    try { await axios.delete(`${API}/tenants/${id}`, { withCredentials: true }); fetchData(); }
    catch { toast.error('Delete failed'); }
  };

  const canManage = MANAGE.includes(user?.role);

  return (
    <Layout>
      <div data-testid="tenants-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">{t('tenant.title')}</h1>
            <p className="text-sm text-muted-foreground">Full lease, contact, and ID records for every occupant.</p>
          </div>
          {canManage && (
            <Dialog open={showDialog} onOpenChange={(o) => { setShowDialog(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
              <DialogTrigger asChild>
                <Button data-testid="new-tenant-btn" onClick={openNew} className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />Add Tenant</Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="tenant-dialog">
                <DialogHeader><DialogTitle>{editingId ? 'Edit' : 'Add'} Tenant</DialogTitle></DialogHeader>
                <TenantForm form={form} setForm={setForm} upd={upd} buildings={buildings}
                            uploadIdDoc={uploadIdDoc} removeIdDoc={removeIdDoc} uploading={uploadingIdx} />
                <div className="flex gap-2 justify-end pt-3 border-t">
                  <Button variant="outline" onClick={() => { setShowDialog(false); setEditingId(null); setForm(EMPTY); }}>Cancel</Button>
                  <Button data-testid="submit-tenant-btn" onClick={handleSave} disabled={!form.tenant_name || !form.building_id || !form.unit_number} className="bg-teal-600 hover:bg-teal-700">{editingId ? 'Save' : 'Add Tenant'}</Button>
                </div>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading…</div> : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((tn) => {
              const b = buildings.find((x) => x.id === tn.building_id);
              const expSoon = tn.lease_end && (new Date(tn.lease_end).getTime() - Date.now()) / (1000 * 60 * 60 * 24) <= 30;
              return (
                <Card key={tn.id} data-testid={`tenant-card-${tn.id}`} className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3 flex-1">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center text-white font-bold text-sm">{tn.tenant_name?.charAt(0)}</div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-foreground truncate">{tn.tenant_name}</h3>
                        <p className="text-xs text-muted-foreground truncate">Unit {tn.unit_number} · {b?.name || 'Building'}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="sm" variant="ghost" onClick={() => setViewing(tn)} data-testid={`view-tenant-${tn.id}`}><Eye className="w-3.5 h-3.5" /></Button>
                      {canManage && (
                        <RowActions
                          testIdPrefix={`tenant-${tn.id}`}
                          onEdit={() => openEdit(tn)}
                          onDelete={() => handleDelete(tn.id)}
                          deleteLabel={`tenant "${tn.tenant_name}"`}
                        />
                      )}
                    </div>
                  </div>
                  <div className="space-y-1 text-xs text-muted-foreground">
                    {tn.contact_phone && <div className="flex items-center gap-2"><Phone className="w-3 h-3" />{tn.contact_phone}</div>}
                    {tn.whatsapp_number && <div className="flex items-center gap-2 text-emerald-600"><MessageCircle className="w-3 h-3" />{tn.whatsapp_number}</div>}
                    {tn.contact_email && <div className="flex items-center gap-2 truncate"><Mail className="w-3 h-3" />{tn.contact_email}</div>}
                    {tn.rent_amount && <div className="flex items-center gap-2 text-slate-700"><DollarSign className="w-3 h-3" />{tn.rent_currency || 'AED'} {tn.rent_amount.toLocaleString()} / {tn.rent_frequency || 'monthly'}</div>}
                    {tn.lease_end && <div className={`flex items-center gap-2 ${expSoon ? 'text-amber-700 font-semibold' : ''}`}><Calendar className="w-3 h-3" />Lease ends {new Date(tn.lease_end).toLocaleDateString()}{expSoon && ' · Renewal due!'}</div>}
                    {tn.home_country && <div className="flex items-center gap-2"><Home className="w-3 h-3" />{tn.home_country}</div>}
                    {tn.id_document_urls?.length > 0 && <div className="flex items-center gap-2 text-teal-700"><IdCard className="w-3 h-3" />{tn.id_document_urls.length} document(s)</div>}
                  </div>
                  <span className={`inline-block mt-3 px-2 py-0.5 rounded-full text-xs font-semibold ${tn.status === 'active' ? 'bg-green-100 text-green-800' : tn.status === 'notice_served' ? 'bg-amber-100 text-amber-800' : 'bg-slate-100 text-slate-700'}`}>{tn.status}</span>
                </Card>
              );
            })}
            {items.length === 0 && (
              <Card className="p-10 text-center col-span-full">
                <UserCheck className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No tenants yet</p>
              </Card>
            )}
          </div>
        )}

        {/* Detail view */}
        {viewing && (
          <Dialog open onOpenChange={() => setViewing(null)}>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="tenant-view-dialog">
              <DialogHeader><DialogTitle className="flex items-center gap-2"><UserCheck className="w-5 h-5 text-teal-600" />{viewing.tenant_name}</DialogTitle></DialogHeader>
              <TenantDetail tenant={viewing} building={buildings.find(b => b.id === viewing.building_id)} />
              <div className="flex justify-end pt-3 border-t"><Button onClick={() => setViewing(null)} className="bg-teal-600 hover:bg-teal-700">Close</Button></div>
            </DialogContent>
          </Dialog>
        )}
      </div>
    </Layout>
  );
}

/* ------------------------- Form ------------------------- */

function Section({ icon, title, children }) {
  return (
    <div className="border rounded-lg p-3 space-y-2">
      <div className="text-xs font-semibold uppercase tracking-wide text-teal-700 flex items-center gap-1.5">{icon}{title}</div>
      {children}
    </div>
  );
}

function TenantForm({ form, setForm, upd, buildings, uploadIdDoc, removeIdDoc, uploading }) {
  return (
    <div className="space-y-3 mt-2">
      <Section icon={<UserCheck className="w-3.5 h-3.5" />} title="Basic Info">
        <div><Label>Tenant Name *</Label><Input data-testid="tf-name" value={form.tenant_name} onChange={upd('tenant_name')} /></div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Building *</Label>
            <Select value={form.building_id} onValueChange={(v) => setForm({ ...form, building_id: v })}>
              <SelectTrigger data-testid="tf-building"><SelectValue placeholder="Building" /></SelectTrigger>
              <SelectContent>{buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Unit Number *</Label><Input data-testid="tf-unit" value={form.unit_number} onChange={upd('unit_number')} placeholder="e.g., 1204" /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Unit Type</Label>
            <Select value={form.unit_type || ''} onValueChange={(v) => setForm({ ...form, unit_type: v })}>
              <SelectTrigger><SelectValue placeholder="Studio / 1BR / …" /></SelectTrigger>
              <SelectContent>{UNIT_TYPES.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Area (sqft)</Label><Input type="number" value={form.unit_area_sqft} onChange={upd('unit_area_sqft')} placeholder="e.g., 950" /></div>
        </div>
        <div><Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="notice_served">Notice served</SelectItem>
              <SelectItem value="vacated">Vacated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Section>

      <Section icon={<Phone className="w-3.5 h-3.5" />} title="Contact">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input type="tel" data-testid="tf-phone" value={form.contact_phone} onChange={upd('contact_phone')} placeholder="+971 50 123 4567" /></div>
          <div><Label>WhatsApp</Label><Input type="tel" data-testid="tf-whatsapp" value={form.whatsapp_number} onChange={upd('whatsapp_number')} placeholder="+971 50 123 4567" /></div>
        </div>
        <div><Label>Email</Label><Input type="email" value={form.contact_email} onChange={upd('contact_email')} /></div>
      </Section>

      <Section icon={<DollarSign className="w-3.5 h-3.5" />} title="Lease & Rent">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Lease Start</Label><Input type="date" data-testid="tf-lease-start" value={form.lease_start} onChange={upd('lease_start')} /></div>
          <div><Label>Lease End</Label><Input type="date" data-testid="tf-lease-end" value={form.lease_end} onChange={upd('lease_end')} /></div>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <div><Label>Rent Amount</Label><Input type="number" data-testid="tf-rent" value={form.rent_amount} onChange={upd('rent_amount')} placeholder="e.g., 6500" /></div>
          <div><Label>Currency</Label><Input value={form.rent_currency} onChange={upd('rent_currency')} placeholder="AED" /></div>
          <div><Label>Frequency</Label>
            <Select value={form.rent_frequency} onValueChange={(v) => setForm({ ...form, rent_frequency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{RENT_FREQS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Deposit Amount</Label><Input type="number" value={form.deposit_amount} onChange={upd('deposit_amount')} /></div>
          <div className="flex items-end gap-2">
            <input type="checkbox" id="tf-deposit-paid" data-testid="tf-deposit-paid" className="w-4 h-4 accent-teal-600" checked={!!form.deposit_paid} onChange={(e) => setForm({ ...form, deposit_paid: e.target.checked })} />
            <label htmlFor="tf-deposit-paid" className="text-sm">Deposit received</label>
          </div>
        </div>
      </Section>

      <Section icon={<UserCheck className="w-3.5 h-3.5" />} title="Alternate / Emergency Contact">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Name</Label><Input data-testid="tf-alt-name" value={form.alternate_contact_name} onChange={upd('alternate_contact_name')} /></div>
          <div><Label>Relation</Label>
            <Select value={form.alternate_contact_relation || ''} onValueChange={(v) => setForm({ ...form, alternate_contact_relation: v })}>
              <SelectTrigger><SelectValue placeholder="Spouse / Parent / …" /></SelectTrigger>
              <SelectContent>{REL_OPTIONS.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Phone</Label><Input type="tel" value={form.alternate_contact_phone} onChange={upd('alternate_contact_phone')} /></div>
          <div><Label>Email</Label><Input type="email" value={form.alternate_contact_email} onChange={upd('alternate_contact_email')} /></div>
        </div>
      </Section>

      <Section icon={<Home className="w-3.5 h-3.5" />} title="Home Country">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Country</Label><Input data-testid="tf-home-country" value={form.home_country} onChange={upd('home_country')} placeholder="e.g., India" /></div>
          <div><Label>Phone</Label><Input type="tel" value={form.home_country_phone} onChange={upd('home_country_phone')} placeholder="+91 ..." /></div>
        </div>
        <div><Label>Address</Label><Input value={form.home_country_address} onChange={upd('home_country_address')} placeholder="Street, City, State, Postcode" /></div>
      </Section>

      <Section icon={<IdCard className="w-3.5 h-3.5" />} title="Identification Documents">
        <div className="grid grid-cols-2 gap-3">
          <div><Label>ID Type</Label>
            <Select value={form.id_document_type} onValueChange={(v) => setForm({ ...form, id_document_type: v })}>
              <SelectTrigger data-testid="tf-id-type"><SelectValue /></SelectTrigger>
              <SelectContent>{ID_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>ID Number</Label><Input data-testid="tf-id-number" value={form.id_document_number} onChange={upd('id_document_number')} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>ID Expiry</Label><Input type="date" value={form.id_document_expiry} onChange={upd('id_document_expiry')} /></div>
          <div><Label>Emirates ID (if applicable)</Label><Input value={form.emirates_id} onChange={upd('emirates_id')} placeholder="784-YYYY-NNNNNNN-N" /></div>
        </div>
        <div>
          <Label>Uploaded Documents ({(form.id_document_urls || []).length})</Label>
          <div className="flex flex-wrap gap-2 mt-1">
            {(form.id_document_urls || []).map((url, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-1 bg-teal-50 border border-teal-200 rounded text-xs">
                <a href={url} target="_blank" rel="noopener noreferrer" className="text-teal-700 underline">Doc {i + 1}</a>
                <button type="button" onClick={() => removeIdDoc(i)} className="text-rose-600 hover:text-rose-800"><X className="w-3 h-3" /></button>
              </div>
            ))}
            <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 border border-dashed border-teal-400 rounded text-xs text-teal-700 hover:bg-teal-50">
              {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              {uploading ? 'Uploading…' : 'Upload'}
              <input data-testid="tf-id-upload" type="file" className="hidden" accept="image/*,.pdf" onChange={(e) => { uploadIdDoc(e.target.files?.[0]); e.target.value = ''; }} disabled={uploading} />
            </label>
          </div>
          <p className="text-[10px] text-muted-foreground mt-1">Passport pages, Emirates ID front &amp; back, driving license — up to 20 MB each.</p>
        </div>
      </Section>

      <Section icon={<FileText className="w-3.5 h-3.5" />} title="Notes">
        <textarea rows={2} className="w-full text-sm border rounded px-2 py-1.5" value={form.notes || ''} onChange={upd('notes')} placeholder="Anything the property manager should know…" />
      </Section>
    </div>
  );
}

/* ------------------------- View ------------------------- */

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="text-sm">
      <span className="text-slate-500 text-xs uppercase tracking-wide font-semibold">{label}: </span>
      <span className="text-slate-800">{value}</span>
    </div>
  );
}

function TenantDetail({ tenant, building }) {
  return (
    <div className="space-y-3 mt-2 text-sm">
      <div className="grid grid-cols-2 gap-3">
        <Row label="Building" value={building?.name} />
        <Row label="Unit" value={`${tenant.unit_number}${tenant.unit_type ? ` (${tenant.unit_type})` : ''}${tenant.unit_area_sqft ? ` · ${tenant.unit_area_sqft} sqft` : ''}`} />
        <Row label="Status" value={tenant.status} />
      </div>
      <div className="border-t pt-2 grid grid-cols-2 gap-3">
        <Row label="Phone" value={tenant.contact_phone} />
        <Row label="WhatsApp" value={tenant.whatsapp_number} />
        <Row label="Email" value={tenant.contact_email} />
      </div>
      {(tenant.lease_start || tenant.lease_end || tenant.rent_amount) && (
        <div className="border-t pt-2 grid grid-cols-2 gap-3">
          <Row label="Lease Start" value={tenant.lease_start ? new Date(tenant.lease_start).toLocaleDateString() : null} />
          <Row label="Lease End" value={tenant.lease_end ? new Date(tenant.lease_end).toLocaleDateString() : null} />
          <Row label="Rent" value={tenant.rent_amount ? `${tenant.rent_currency || 'AED'} ${tenant.rent_amount.toLocaleString()} / ${tenant.rent_frequency}` : null} />
          <Row label="Deposit" value={tenant.deposit_amount ? `${tenant.rent_currency || 'AED'} ${tenant.deposit_amount.toLocaleString()}${tenant.deposit_paid ? ' · Paid' : ' · Unpaid'}` : null} />
        </div>
      )}
      {(tenant.alternate_contact_name || tenant.alternate_contact_phone) && (
        <div className="border-t pt-2">
          <div className="text-xs font-semibold uppercase text-teal-700 mb-1">Alternate contact</div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Name" value={tenant.alternate_contact_name} />
            <Row label="Relation" value={tenant.alternate_contact_relation} />
            <Row label="Phone" value={tenant.alternate_contact_phone} />
            <Row label="Email" value={tenant.alternate_contact_email} />
          </div>
        </div>
      )}
      {(tenant.home_country || tenant.home_country_phone) && (
        <div className="border-t pt-2">
          <div className="text-xs font-semibold uppercase text-teal-700 mb-1">Home country</div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Country" value={tenant.home_country} />
            <Row label="Phone" value={tenant.home_country_phone} />
          </div>
          <Row label="Address" value={tenant.home_country_address} />
        </div>
      )}
      {(tenant.id_document_type || tenant.id_document_urls?.length > 0) && (
        <div className="border-t pt-2">
          <div className="text-xs font-semibold uppercase text-teal-700 mb-1">ID document</div>
          <div className="grid grid-cols-2 gap-3">
            <Row label="Type" value={tenant.id_document_type} />
            <Row label="Number" value={tenant.id_document_number} />
            <Row label="Expiry" value={tenant.id_document_expiry ? new Date(tenant.id_document_expiry).toLocaleDateString() : null} />
            <Row label="Emirates ID" value={tenant.emirates_id} />
          </div>
          {tenant.id_document_urls?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {tenant.id_document_urls.map((url, i) => (
                <a key={i} href={url} target="_blank" rel="noopener noreferrer" className="text-xs px-2 py-1 rounded bg-teal-50 border border-teal-200 text-teal-700 underline">📎 Document {i + 1}</a>
              ))}
            </div>
          )}
        </div>
      )}
      {tenant.notes && <div className="border-t pt-2"><div className="text-xs font-semibold uppercase text-teal-700 mb-1">Notes</div><p className="text-slate-700 whitespace-pre-wrap">{tenant.notes}</p></div>}
    </div>
  );
}
